import { useEffect, useRef } from "react";
import { Flame, X } from "lucide-react";
import { Button } from "./ui/button";

// Milestone-specific copy. Warm, personal, non-braggy — we're
// celebrating the *behaviour* (showing up), not the number.
const MESSAGES = {
  3: {
    title: "Three days in a row.",
    body: "That's not luck — that's you deciding, three times, that your nervous system matters. This is how change actually happens.",
    accent: "Keep going",
  },
  7: {
    title: "A week of showing up.",
    body: "You've built a real habit. The version of you a month from now is quietly rooting for the version of you who kept going this week.",
    accent: "You did this",
  },
  14: {
    title: "Two weeks strong.",
    body: "Bilateral practice at this cadence is when the changes stop feeling like effort and start feeling like who you are.",
    accent: "This is your practice now",
  },
  30: {
    title: "Thirty days.",
    body: "You've moved from trying it to living it. Your future self already thanks you for the mornings and evenings you chose calm.",
    accent: "You changed something real",
  },
};

/**
 * Full-screen celebration modal for milestone streak days (3/7/14/30).
 *
 * Rendered by Dashboard whenever `milestone` prop is set. Uses a
 * CSS-only confetti burst (no runtime library) so we don't add to the
 * iOS bundle. The parent is responsible for:
 *   • Calling markCelebrated(milestone) on close, so we don't re-show it.
 *   • Only mounting the modal when milestone is one of MESSAGES keys.
 */
export const StreakCelebrationModal = ({ milestone, streak, userName, onClose }) => {
  const closeBtnRef = useRef(null);
  const copy = MESSAGES[milestone] || MESSAGES[3];

  // Focus the close button when the modal opens so tabbing / Escape
  // works predictably. Escape also closes.
  useEffect(() => {
    closeBtnRef.current?.focus?.();
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="streak-modal-title"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[#1B2349]/70 backdrop-blur-sm px-4 animate-fade-in"
      data-testid="streak-celebration-modal"
      data-milestone={milestone}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      {/* Confetti — 24 amber/teal/cream squares falling from the top.
          CSS-only so no runtime dep. */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {Array.from({ length: 24 }).map((_, i) => (
          <span
            key={i}
            className="absolute top-[-20px] w-2 h-3 rounded-sm animate-confetti-fall"
            style={{
              left: `${(i * 4.3) % 100}%`,
              backgroundColor: ["#F6C071", "#3090A8", "#E2B887", "#78C0D8", "#F5EEE0"][i % 5],
              animationDelay: `${(i % 8) * 0.15}s`,
              transform: `rotate(${(i * 47) % 360}deg)`,
            }}
          />
        ))}
      </div>

      <div className="relative w-full max-w-md bg-[#FBF8F1] rounded-3xl shadow-2xl p-8 sm:p-10 animate-fade-in-up">
        <button
          ref={closeBtnRef}
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 rounded-full hover:bg-white active:bg-slate-100 flex items-center justify-center transition-colors"
          aria-label="Close celebration"
          data-testid="streak-modal-close"
        >
          <X className="w-4 h-4 text-[#718096]" />
        </button>

        {/* Streak number ring */}
        <div className="flex flex-col items-center text-center">
          <div className="relative w-24 h-24 mb-5">
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#F6C071] to-[#E29B4C] animate-pulse-slow" />
            <div className="absolute inset-1 rounded-full bg-[#FBF8F1] flex flex-col items-center justify-center">
              <Flame className="w-5 h-5 text-[#E29B4C]" aria-hidden />
              <span
                className="text-2xl font-outfit font-semibold text-[#1B2349] tabular-nums leading-none mt-0.5"
                data-testid="streak-modal-count"
              >
                {streak ?? milestone}
              </span>
              <span className="text-[10px] uppercase tracking-wider text-[#B08D5A] mt-0.5">
                {streak === 1 ? "day" : "days"}
              </span>
            </div>
          </div>

          <div
            className="text-xs uppercase tracking-widest text-[#E29B4C] font-medium mb-2"
            data-testid="streak-modal-accent"
          >
            {copy.accent}
          </div>
          <h2
            id="streak-modal-title"
            className="font-outfit font-semibold text-2xl sm:text-3xl text-[#1B2349] mb-3"
            data-testid="streak-modal-title"
          >
            {copy.title}
          </h2>
          <p
            className="text-[#4A5568] text-base leading-relaxed"
            data-testid="streak-modal-body"
          >
            {userName ? `${userName}, ` : ""}
            {userName ? copy.body.charAt(0).toLowerCase() + copy.body.slice(1) : copy.body}
          </p>

          <Button
            onClick={onClose}
            className="mt-8 rounded-full bg-[#1B2349] text-white hover:bg-[#0F1838] px-6"
            data-testid="streak-modal-primary-btn"
          >
            Keep going
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StreakCelebrationModal;
